import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { useMutation, useQuery } from "@apollo/client";
import { ForecastMonth } from "arps_wasm";
import {
  CLONE_TYPE_WELL_ERROR_MESSAGE,
  SAVE_TYPE_WELL_ERROR_MESSAGE
} from "constants/errorMessages.constants";
import { TYPE_WELLS } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import {
  selectCheckedGlobalTypeWells,
  selectIsTypeWellSavable,
  selectSelectedTypeWell,
  selectTemporaryForecast,
  useForecastStore
} from "stores/forecast";
import {
  toExpectedForecastBackendProductName,
  toExpectedTypeWellBackendProductName
} from "utils/arps/productMapping";
import { v4 as uuidv4 } from "uuid";

import { TYPE_WELL_PERFORMANCE_QUERY_KEY } from "api/typeWellPerformance";
import { FOLDERS, SAVE_FORECAST } from "api/userArps";

import { ArpSegment, UserArpsItem } from "models/UserArpsModel";
import { EntityKind } from "models/entityKind";

import { SaveResult } from "components/project/forecasts/ProjectForecasts";
import { useSelectedProject } from "components/project/projects/hooks";
import { useWellList } from "components/well-list/context";

import { MoveTypeWellInput } from "../../../models/projects";
import "../context/reducer/arpsReducer";
import { convertConstantsToDatabaseUnits } from "../utils/convertConstantsToDatabaseUnits";
import useTypeWellUpdater from "./useTypeWellUpdater";

export default function useTypeWellSaver(undoRedoManager) {
  const queryClient = useQueryClient();

  const isTypeWellSavable = useForecastStore(selectIsTypeWellSavable);
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const temporaryForecast = useForecastStore(selectTemporaryForecast);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const setOriginalCheckedGlobalTypeWells = useForecastStore(
    (s) => s.setOriginalCheckedGlobalTypeWells
  );
  const setTemporaryForecast = useForecastStore((s) => s.setTemporaryForecast);
  const setIsTypeWellSavable = useForecastStore((s) => s.setIsTypeWellSavable);
  const setHasTypeWellSaveSucceeded = useForecastStore(
    (s) => s.setHasTypeWellSaveSucceeded
  );
  const fsSetSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);

  const filterState = useSelector((state: RootState) => {
    const obj = {
      ...(state.filter.polygonFilter?.source && {
        polygonFilter: state.filter.polygonFilter
      }),
      excludePolygonsFilter: state.filter.excludePolygonsFilter,
      wellListFilter: state.filter.wellListFilter,
      propertiesFilter: state.filter.propertiesFilter,
      entityKind: EntityKind.Well
    };
    return obj;
  });

  // Will possible  need to refactor to global state for popup to work
  const [wellListState] = useWellList();
  const { changeSelectedTypeWell } = useTypeWellUpdater(undoRedoManager);

  const { selectedProject: project } = useSelectedProject();

  const [saveForecast, status] = useMutation<SaveResult>(SAVE_FORECAST, {
    refetchQueries: ["changeRecords", "wellListRecords"]
  });

  const { refetch } = useQuery(FOLDERS, {
    variables: {
      req: {
        projectId: project?.projectId,
        type: TYPE_WELLS
      }
    },
    skip: !project?.projectId
  });

  useEffect(() => {
    if (isTypeWellSavable) {
      setHasTypeWellSaveSucceeded(false);
    }
  }, [isTypeWellSavable]);

  async function onCloneTypeWell(
    newTypeWellTitle: string,
    moveTypeWellToProjectInput?: MoveTypeWellInput,
    forecastArray?: ForecastMonth[]
  ) {
    try {
      const id = uuidv4();
      const result = await saveTypeWellInternal(
        selectedTypeWell,
        id,
        newTypeWellTitle,
        !!selectedTypeWell?.source,
        moveTypeWellToProjectInput,
        forecastArray
      );

      await refetch({
        req: {
          projectId: project?.projectId,
          type: TYPE_WELLS
        }
      });

      const tw: UserArpsItem = {
        ...selectedTypeWell,
        id: id,
        key: id,
        uniqueID: id,
        title: newTypeWellTitle,
        source: null
      };

      if (moveTypeWellToProjectInput) {
        changeSelectedTypeWell(null);
      } else {
        changeSelectedTypeWell(tw);
        // changeSelectedTypeWell closes over checkedGlobalTypeWells/checkedTypeWellKeys
        // from the last render (stale) and appends tw to them. Reset both to only
        // the new clone to match the pre-migration setCheckedKeys + setCheckedGlobalTypeWells
        // reset — the clone should be the sole selected type well.
        setCheckedGlobalTypeWells([tw]);
        setCheckedTypeWellKeys([tw.key]);
      }

      setIsTypeWellSavable(result?.data.saveForecast !== "ok");
      setHasTypeWellSaveSucceeded(result?.data.saveForecast === "ok");

      // Invalidate type well performance cache so charts can fetch data for the new clone
      if (result?.data.saveForecast === "ok") {
        queryClient.invalidateQueries(TYPE_WELL_PERFORMANCE_QUERY_KEY);
      }
    } catch (error) {
      // Log the full error details to Sentry for debugging
      Sentry.captureException(error, {
        tags: {
          feature: "typewell",
          operation: "clone"
        },
        extra: {
          typeWellTitle: newTypeWellTitle,
          hasProjectMove: !!moveTypeWellToProjectInput
        }
      });

      throw new Error(CLONE_TYPE_WELL_ERROR_MESSAGE);
    }
  }

  async function onSaveTypeWell(
    tw?: UserArpsItem,
    newTypeWellTitle?: string,
    shouldRefetch = true
  ) {
    try {
      // Update currently selected type well title on chart.
      if (tw && newTypeWellTitle) {
        const typeWellWithUpdatedTitle = {
          ...tw,
          isEdit: false,
          title: newTypeWellTitle
        };

        const updatedGlobalTypeWells = checkedGlobalTypeWells.map((typeWell) => {
          if (typeWell.id === typeWellWithUpdatedTitle.id) {
            return typeWellWithUpdatedTitle;
          }
          return typeWell;
        });

        setCheckedGlobalTypeWells(updatedGlobalTypeWells);
        // A type well can be renamed without being selected or checked.
        // If it's selected it can show up in the type well widget (if open).
        if (selectedTypeWell?.id === tw.id) {
          fsSetSelectedTypeWell({
            ...selectedTypeWell,
            isEdit: false,
            title: newTypeWellTitle
          });
        }
      }
      const result = await saveTypeWellInternal(tw, null, newTypeWellTitle);

      if (shouldRefetch) {
        await refetch({
          req: {
            projectId: project?.projectId,
            type: TYPE_WELLS
          }
        });
      }

      const saveSuccessful = result?.data.saveForecast === "ok";

      const shouldClearTemporaryForecast =
        saveSuccessful && (!tw || !temporaryForecast || tw.id === temporaryForecast.id);

      if (shouldClearTemporaryForecast) {
        setTemporaryForecast(null);
      }

      setIsTypeWellSavable(!saveSuccessful);
      setHasUnsavedChanges(!saveSuccessful);
      setHasTypeWellSaveSucceeded(saveSuccessful);

      // Clear the backups after successful save
      if (saveSuccessful) {
        setOriginalCheckedGlobalTypeWells([]);
        // Invalidate type well performance cache so charts refetch with new data
        queryClient.invalidateQueries(TYPE_WELL_PERFORMANCE_QUERY_KEY);
      }

      // Return success status so callers can handle failed saves
      return { success: saveSuccessful, result };
    } catch (error) {
      // Log the full error details to Sentry for debugging
      Sentry.captureException(error, {
        tags: {
          feature: "typewell",
          operation: "save"
        },
        extra: {
          typeWellId: tw?.id || selectedTypeWell?.id,
          typeWellTitle: newTypeWellTitle || tw?.title || selectedTypeWell?.title
        }
      });

      // Set failure states
      setIsTypeWellSavable(true);
      setHasUnsavedChanges(true);
      setHasTypeWellSaveSucceeded(false);

      // Return failure status with user-friendly error
      return {
        success: false,
        error: SAVE_TYPE_WELL_ERROR_MESSAGE
      };
    }
  }

  async function saveTypeWellInternal(
    tw?: UserArpsItem,
    id?: string,
    newTypeWellTitle?: string,
    isSync?: boolean,
    moveTypeWellToProjectInput?: MoveTypeWellInput,
    forecastArray?: ForecastMonth[]
  ) {
    const projectId = project?.projectId;
    const data = tw ?? selectedTypeWell;
    const folderId = data?.folderId;

    const wellList = wellListState.wells.map((x) => x.id) ?? [];
    const title = newTypeWellTitle ?? tw?.title ?? selectedTypeWell.title;
    const rescat =
      tw != null && tw.id != selectedTypeWell?.id
        ? data.reserveCategory
        : selectedTypeWell.reserveCategory;

    const forecastId = id ?? data.id;

    const forecastUniqueID = id ?? data.uniqueID;

    // Save As is the only caller that passes an id: it mints a fresh one for the copy while
    // `data` is still the type well being copied. Telling the backend which forecast that
    // was lets it carry the source's well list and change records onto the copy, instead of
    // the copy starting with no history (EVA-159).
    const copyHistoryFromForecastId = id != null && id !== data.id ? data.id : undefined;

    const forecast = {
      id: forecastId,
      owner: "",
      uniqueID: forecastUniqueID,
      name: title,
      arps: data.arps.map((seg) => {
        // convert the date string to ISO format that backend is expected
        let date;
        try {
          date = new Date(Date.parse(seg.startDate)).toISOString();
        } catch {
          date = seg.startDate;
        }

        // clone the segment to avoid mutating the original
        const segCopy = { ...seg };

        // remove q0 as the backend does not expect it
        delete segCopy.q0;

        // Create a clean segment using only valid ArpSegment fields
        // This ensures no template-specific fields or display headers leak through
        const validSegmentFields: (keyof ArpSegment)[] = [
          "product",
          "segmentIndex",
          "startDate",
          "endDate",
          "qi",
          "di",
          "b",
          "n",
          "qf",
          "trendCum",
          "df",
          "slopeType",
          "maxRate",
          "timeCalculated",
          "cumToDate",
          "isUserRatioDecline"
        ];

        const cleanSegment: Record<string, unknown> = {};
        validSegmentFields.forEach((field) => {
          if (segCopy[field] !== null && segCopy[field] !== undefined) {
            cleanSegment[field] = segCopy[field];
          }
        });

        return {
          ...cleanSegment,
          // user-arps service currently requires both b and n values.
          n: segCopy.b,
          startDate: date,
          product: toExpectedForecastBackendProductName(segCopy.product)
        };
      }),
      constants: convertConstantsToDatabaseUnits(data.constants || []).map((fc) => ({
        ...fc,
        product: toExpectedTypeWellBackendProductName(fc.product)
      })),
      wellData: data.wellData,
      color: data.color,
      thickness: data.thickness,
      monthArray: forecastArray ?? data.forecastArray,
      folderId: folderId,
      reserveCategory: rescat,
      createdDate: new Date().toISOString(),
      source: isSync ? null : data.source
    };
    const input = {
      projectId,
      folderId,
      type: TYPE_WELLS,
      forecast,
      wellList,
      moveProject: moveTypeWellToProjectInput,
      filterJSON: JSON.stringify(filterState),
      copyHistoryFromForecastId
    };
    const result = await saveForecast({
      variables: {
        input
      }
    });

    // Validate that we got a proper response
    if (!result || !result.data) {
      throw new Error("Invalid response from save operation");
    }

    return result;
  }

  return { onSaveTypeWell, onCloneTypeWell, saveStatus: status };
}
